import { AfterContentInit, ChangeDetectorRef, Directive, EventEmitter, Input, Output } from "@angular/core";
import {
  addDays,
  endOfDay,
  endOfISOWeek,
  endOfMonth,
  isBefore,
  startOfDay,
  startOfISOWeek,
  startOfMonth,
  subDays,
} from "@vattenfall/util";
const VERY_BIG_NUMBER = 9999999;

export type CalendarType = "day" | "week" | "month" | "months" | "year";

export class DateRange {
  constructor(readonly start: Date, readonly end = new Date()) {}
}

@Directive({
  selector: "[ui-calendar]",
})
export class CalendarDirective implements AfterContentInit {
  constructor(public changeDetectorRef: ChangeDetectorRef) {}

  @Output()
  readonly selectedChange: EventEmitter<DateRange> = new EventEmitter<DateRange>();

  ngAfterContentInit() {
    // Set the default resolution. Show the view that the calendar is initialized with.
    this.resolution = this.type;
    // Set active date (the date that the calendar default screen will be built around)
    this.active = this.selected || new DateRange(new Date());
  }

  @Input() type: CalendarType = "day";

  // Holds the value for what type of calendar this component will render. By default we show a month of days.
  _resolution!: CalendarType;
  @Input()
  get resolution(): CalendarType {
    return this._resolution;
  }
  set resolution(resolution: CalendarType) {
    this._resolution = resolution;
  }

  _dateRange!: DateRange;

  @Input()
  get dateRange(): DateRange {
    return this._dateRange;
  }
  set dateRange(range: DateRange) {
    this._dateRange = range;
  }

  @Output()
  readonly dateRangeChange: EventEmitter<DateRange> = new EventEmitter<DateRange>();

  /**
   * min - returns the earliest selectable date in the calendar.
   */
  _min!: Date;
  @Input()
  get min(): Date {
    // Otherwise create the date from start of the date provided and return
    return this._min ? startOfDay(this._min as Date) : subDays(startOfDay(new Date()), VERY_BIG_NUMBER);
  }
  set min(date: Date) {
    this._min = date;
  }

  /**
   * _max - the maximum selectable date
   */
  _max!: Date;
  @Input()
  get max(): Date {
    return this._max ? endOfDay(this._max) : addDays(startOfDay(new Date()), VERY_BIG_NUMBER);
  }
  /**
   * set max date
   */
  set max(date: Date) {
    this._max = date;
  }

  /**
   * DisabledDateRanges is introduced to allow disabling date ranges.
   * Date ranges are disabled between the minimum and maximum selectable intervals in the calendar.
   */
  _disabledDateRanges?: DateRange[];

  /**
   * returns the Disabled date range
   */
  @Input()
  get disabledDateRanges() {
    return this._disabledDateRanges ?? [];
  }
  /**
   *
   */
  set disabledDateRanges(range: DateRange[]) {
    this._disabledDateRanges = range;
  }

  /**
   * Holds the currently selected range in the calendar.
   */
  _selected!: DateRange;

  /**
   * returns the selected range
   */
  @Input()
  get selected() {
    return this._selected;
  }
  /**
   *
   */
  set selected(range: DateRange) {
    this._active = range;
    this._selected = range;
  }

  _active!: DateRange;
  @Input()
  get active(): DateRange {
    this.active = new DateRange(this._active.start, this._active.end);
    return this._active;
  }
  set active(range: DateRange) {
    this._active = range;
  }

  @Output() readonly activeChange: EventEmitter<DateRange> = new EventEmitter<DateRange>();

  onResolutionChange(resolution: CalendarType) {
    this.resolution = resolution;
    this.changeDetectorRef.detectChanges();
  }
  /**
   * Selects a date range with start and end.
   * @param range - the range with start|end dates.
   */
  selectRange(range: DateRange) {
    // Only emmit change if we are not zoomed in the calendar.
    // If the zoomed legnth is one, then we are expecting that the calendar is just changing resolution via the quick navigation.
    if (this.resolution === this.type) {
      this.selected = range;
      this.selectedChange.emit(range);
      this.active = range;
      this.changeDetectorRef.detectChanges();
    } else {
      this.updateActive(range);
      // if resolution is different to calendar type, that means that we are in zoomed state,
      // reset zoom after updating active to neutralize side effects
      this.resolution = this.type;
      this.changeDetectorRef.detectChanges();
    }
  }

  public updateActive(range: DateRange) {
    const { start: activeStart, end: activeEnd } = this.active;

    switch (this.resolution) {
      case "year": {
        activeStart.setFullYear(range.start.getFullYear());
        activeEnd.setFullYear(range.end.getFullYear());
        break;
      }
      case "month": {
        activeStart.setMonth(range.start.getMonth());
        activeEnd.setMonth(range.end.getMonth());
        break;
      }

      case "day": {
        const { start: activeStart, end: activeEnd } = this.active;
        activeStart.setDate(range.start.getDate());
        activeEnd.setDate(range.end.getDate());
        break;
      }
      default:
        break;
    }
    this.active = new DateRange(activeStart, activeEnd);
  }
  onActiveChange(range: DateRange) {
    this.active = range;
  }
  /**
   * Create a month around the given date (i.e return an all weeks of the month as an array of 7 days. )
   * @param date - the date to build the month around
   * @returns - an array of weeks where each week is an array of days.
   */
  get month(): Date[][] {
    const calendar = [];
    const active = this.active && this.active.start ? this.active.start : new Date();
    const startDate = startOfISOWeek(startOfMonth(active));
    const end = endOfMonth(endOfISOWeek(active));
    // Start with one day before start of month,
    // to correct the issue of momnths starting on a Sunday (ISO standard)
    let current = subDays(startDate, 1);

    while (isBefore(current, end)) {
      calendar.push(
        Array(7)
          .fill(null)
          .map(() => {
            current = addDays(current, 1);
            return current;
          })
      );
    }
    return calendar;
  }

  isValidRange(range: DateRange) {
    return range && range.start && range.end && isBefore(range.start, range.end);
  }
}
